import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Check, X, Play, Square, RotateCw, ExternalLink, GitBranch, Lock, SkipForward,
} from 'lucide-react';
import { RubyGemMark } from './RubyGemMark';
import { RubyLoader } from './RubyLoader';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface RunStep {
  name: string;
  status: string;                 // queued | in_progress | completed
  conclusion: string | null;      // success | failure | skipped | cancelled | null
  number: number;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface RunJob {
  id: number;
  name: string;
  status: string;
  conclusion: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  steps?: RunStep[];
}

export interface WorkflowRun {
  id?: number;
  name?: string;
  status?: string;
  conclusion?: string | null;
  run_number?: number;
  head_branch?: string;
  head_sha?: string;
  event?: string;
  html_url?: string;
  created_at?: string;
  run_started_at?: string;
  updated_at?: string;
}

export interface BranchGraph {
  branches: { name: string; sha: string; color?: string }[];
  commits: { sha: string; message: string; author?: string; date: string; parents: string[] }[];
}

export type StepMarker = 'done' | 'failed' | 'skipped' | 'running' | 'pending';

export interface StepLine {
  key: string;
  name: string;
  jobName?: string;
  marker: StepMarker;
  durationSec: number | null;
  isActive: boolean;
}

export interface BranchSummaryRow {
  name: string;
  sha: string;
  message: string;
  date: string | null;
  isCurrent: boolean;
}

export interface BranchSummary {
  branches: BranchSummaryRow[];
  lastMerge: { sha: string; message: string; date: string } | null;
}

export interface PipelineTooltipActions {
  onStart: () => void;
  onStop: () => void;
  onRerun: () => void;
  loadBranchGraph: () => Promise<BranchGraph>;
}

// ── Pure helpers (unit-tested) ─────────────────────────────────────────────────

/** Seconds → `M:SS`, or `H:MM:SS` once past an hour. Never renders a negative. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hrs > 0 ? `${hrs}:${pad(mins)}:${pad(secs)}` : `${mins}:${pad(secs)}`;
}

/**
 * Elapsed seconds between a start and either its completion or `now`.
 * `now` is passed in rather than read, so the ticking clock stays testable.
 */
export function elapsedSeconds(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  now: number,
): number | null {
  if (!startIso) return null;
  const start = new Date(startIso).getTime();
  if (Number.isNaN(start)) return null;
  const end = endIso ? new Date(endIso).getTime() : now;
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.floor((end - start) / 1000));
}

export function shortSha(sha: string | null | undefined): string {
  return sha ? sha.slice(0, 7) : '';
}

function stepMarker(step: RunStep): StepMarker {
  if (step.status === 'in_progress') return 'running';
  if (step.status !== 'completed') return 'pending';
  if (step.conclusion === 'success') return 'done';
  if (step.conclusion === 'skipped') return 'skipped';
  if (step.conclusion === null) return 'pending';
  return 'failed';   // failure | cancelled | timed_out
}

/**
 * Flatten every job's steps into the lines the log window renders.
 * The first running step is the active one — that's what we pulse and scroll to.
 * Job names only prefix lines when a run has more than one job.
 */
export function deriveStepLines(jobs: RunJob[] | undefined | null, now: number): StepLine[] {
  if (!jobs || jobs.length === 0) return [];
  const multiJob = jobs.length > 1;
  const lines: StepLine[] = [];
  let activeTaken = false;

  for (const job of jobs) {
    for (const step of job.steps || []) {
      const marker = stepMarker(step);
      const isActive = marker === 'running' && !activeTaken;
      if (isActive) activeTaken = true;
      lines.push({
        key: `${job.id}-${step.number}-${step.name}`,
        name: step.name,
        jobName: multiJob ? job.name : undefined,
        marker,
        durationSec: elapsedSeconds(step.started_at, step.completed_at, now),
        isActive,
      });
    }
  }
  return lines;
}

/**
 * Join branch heads to their commit, and pick out the newest merge commit
 * (a commit with more than one parent) as the last merge.
 */
export function summarizeBranchGraph(
  graph: BranchGraph | null | undefined,
  currentBranch?: string,
): BranchSummary {
  if (!graph) return { branches: [], lastMerge: null };

  const bySha = new Map(graph.commits.map(c => [c.sha, c]));
  const branches: BranchSummaryRow[] = (graph.branches || []).map(b => {
    const commit = bySha.get(b.sha);
    return {
      name: b.name,
      sha: shortSha(b.sha),
      message: commit?.message || '',
      date: commit?.date || null,
      isCurrent: !!currentBranch && b.name === currentBranch,
    };
  });

  const merges = (graph.commits || [])
    .filter(c => (c.parents?.length || 0) > 1 && c.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const newest = merges[0];
  return {
    branches,
    lastMerge: newest
      ? { sha: shortSha(newest.sha), message: newest.message, date: newest.date }
      : null,
  };
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ── Single-pin store ───────────────────────────────────────────────────────────
// Only one tooltip may be pinned at a time across the whole page.

let pinnedKey: string | null = null;
const pinListeners = new Set<() => void>();

function setPinnedKey(key: string | null) {
  pinnedKey = key;
  pinListeners.forEach(fn => fn());
}

function usePinnedKey(): string | null {
  const [key, setKey] = useState(pinnedKey);
  useEffect(() => {
    const listener = () => setKey(pinnedKey);
    pinListeners.add(listener);
    return () => { pinListeners.delete(listener); };
  }, []);
  return key;
}

// ── Panel pieces ───────────────────────────────────────────────────────────────

const MARKER_ICON: Record<StepMarker, ReactNode> = {
  done: <Check className="w-2.5 h-2.5 text-green-500" />,
  failed: <X className="w-2.5 h-2.5 text-red-500" />,
  skipped: <SkipForward className="w-2.5 h-2.5 text-stone-400" />,
  running: <RubyGemMark size={10} drawing />,
  pending: <span className="w-1 h-1 rounded-full bg-stone-300" />,
};

function StepWindow({ lines }: { lines: StepLine[] }) {
  const activeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [lines.find(l => l.isActive)?.key]);

  if (lines.length === 0) {
    return (
      <div className="rounded border border-stone-200 bg-stone-50 px-2 py-3 text-center text-[10px] text-stone-400">
        No step data for this run
      </div>
    );
  }

  return (
    // ~5.5 lines tall, then scrolls — deliberately a peephole, not a log viewer
    <div className="max-h-[92px] overflow-y-auto rounded border border-stone-200 bg-stone-50 py-1">
      {lines.map(line => (
        <div
          key={line.key}
          ref={line.isActive ? activeRef : undefined}
          className={`flex items-center gap-1.5 px-2 py-[1px] ${line.isActive ? 'bg-yellow-50' : ''}`}
        >
          <span className={`flex w-3 shrink-0 items-center justify-center ${line.isActive ? 'animate-pulse' : ''}`}>
            {MARKER_ICON[line.marker]}
          </span>
          <span
            className={`flex-1 truncate font-mono text-[10px] ${
              line.marker === 'pending' ? 'text-stone-400'
                : line.marker === 'failed' ? 'text-red-600'
                : line.isActive ? 'text-stone-800' : 'text-stone-600'
            }`}
            title={line.jobName ? `${line.jobName} · ${line.name}` : line.name}
          >
            {line.jobName ? <span className="text-stone-400">{line.jobName} · </span> : null}
            {line.name}
          </span>
          {line.durationSec !== null && (
            <span className="shrink-0 font-mono text-[9px] tabular-nums text-stone-400">
              {formatDuration(line.durationSec)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function BranchBlock({
  loading, error, summary,
}: { loading: boolean; error: string; summary: BranchSummary }) {
  if (loading) {
    return (
      <div className="relative space-y-1">
        <RubyLoader variant="overlay" size={20} />
        {[0, 1, 2].map(i => (
          <div key={i} className="h-3 animate-pulse rounded bg-stone-100" style={{ width: `${80 - i * 12}%` }} />
        ))}
      </div>
    );
  }
  if (error) return <p className="text-[10px] text-red-500">{error}</p>;
  if (summary.branches.length === 0) return <p className="text-[10px] text-stone-400">No branch data</p>;

  return (
    <div className="space-y-0.5">
      {summary.branches.map(b => (
        <div key={b.name} className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              b.isCurrent ? 'bg-yellow-400 animate-pulse' : 'bg-stone-300'
            }`}
          />
          <span className={`w-[92px] shrink-0 truncate font-mono text-[10px] ${b.isCurrent ? 'text-stone-800' : 'text-stone-500'}`}>
            {b.name}
          </span>
          <span className="shrink-0 font-mono text-[9px] text-stone-400">{b.sha}</span>
          <span className="flex-1 truncate text-[9px] text-stone-400" title={b.message}>{b.message}</span>
          <span className="shrink-0 text-[9px] text-stone-400">{timeAgo(b.date)}</span>
        </div>
      ))}
      {summary.lastMerge && (
        <p className="pt-0.5 text-[9px] text-stone-400">
          last merge <span className="font-mono">{summary.lastMerge.sha}</span> · {timeAgo(summary.lastMerge.date)}
        </p>
      )}
    </div>
  );
}

// ── Wrapper ────────────────────────────────────────────────────────────────────

const PANEL_WIDTH = 380;
const OPEN_DELAY = 150;
const CLOSE_GRACE = 200;

export function PipelineTooltip({
  tooltipKey,
  repoLabel,
  env,
  regionLabel,
  run,
  jobs,
  isRunning,
  deployLocked,
  actions,
  children,
}: {
  tooltipKey: string;
  repoLabel: string;
  env: 'dev' | 'prod';
  regionLabel: string;
  run?: WorkflowRun | null;
  jobs?: RunJob[];
  isRunning: boolean;
  deployLocked?: boolean;
  actions: PipelineTooltipActions;
  children: ReactNode;
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [graph, setGraph] = useState<BranchGraph | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState('');

  const globalPinned = usePinnedKey();
  const pinned = globalPinned === tooltipKey;
  const open = hovering || pinned;

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  useEffect(() => clearTimers, []);

  const place = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - PANEL_WIDTH - 8);
    // Flip above the row when there isn't room below.
    const below = window.innerHeight - rect.bottom;
    const top = below < 300 ? Math.max(8, rect.top - 300 - 6) : rect.bottom + 6;
    setPos({ top, left });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => place();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, place]);

  // Live clock — only while a tooltip is open on a run that is still going.
  useEffect(() => {
    if (!open || !isRunning) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open, isRunning]);

  // Escape closes a pinned panel (custom handler — never a native dialog).
  useEffect(() => {
    if (!pinned) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPinnedKey(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinned]);

  // `actions` is rebuilt inline by the dashboard on every render, so it is held
  // in a ref: an effect that depended on its identity would tear down and
  // re-run mid-fetch, stranding the panel in a permanent loading state.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // Branch graph is fetched only on pin — one GitHub call per repo, then cached
  // for as long as the row stays mounted.
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!pinned || fetchedRef.current) return;
    fetchedRef.current = true;
    setGraphLoading(true);
    setGraphError('');
    actionsRef.current.loadBranchGraph()
      .then(setGraph)
      .catch(err => {
        setGraphError(err?.message || 'Could not load branches');
        fetchedRef.current = false;   // let the next pin retry
      })
      .finally(() => setGraphLoading(false));
  }, [pinned]);

  const handleEnter = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setHovering(true), OPEN_DELAY);
  };
  const handleLeave = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setHovering(false), CLOSE_GRACE);
  };
  const handleClick = () => {
    clearTimers();
    setHovering(false);
    setPinnedKey(pinned ? null : tooltipKey);
  };

  const lines = deriveStepLines(jobs, now);
  const summary = summarizeBranchGraph(graph, run?.head_branch);
  const runElapsed = elapsedSeconds(
    run?.run_started_at || run?.created_at,
    isRunning ? null : run?.updated_at,
    now,
  );

  const envAccent = env === 'dev' ? 'bg-emerald-500' : 'bg-rose-500';
  const envText = env === 'dev' ? 'text-emerald-600' : 'text-rose-600';

  const panel = open && pos ? createPortal(
    <div
      className="fixed z-[70] rounded-lg border border-stone-200 bg-white shadow-xl"
      style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH }}
      onMouseEnter={() => { if (!pinned) { clearTimers(); setHovering(true); } }}
      onMouseLeave={() => { if (!pinned) handleLeave(); }}
      // React portals bubble events through the React tree, so without this a
      // click on Start/Stop/Rerun or ✕ would also reach the anchor and toggle
      // the pin back off.
      onClick={e => e.stopPropagation()}
      role="dialog"
      aria-label={`${repoLabel} ${env} pipeline`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-stone-100 px-3 py-2">
        <span className={`h-4 w-0.5 shrink-0 rounded-full ${envAccent}`} />
        <span className="flex-1 truncate text-[11px] font-medium text-stone-700">{repoLabel}</span>
        <span className={`shrink-0 text-[10px] font-medium uppercase ${envText}`}>{env}</span>
        <span className="shrink-0 font-mono text-[9px] text-stone-400">{regionLabel}</span>
        {pinned && (
          <button
            onClick={() => setPinnedKey(null)}
            className="shrink-0 rounded p-0.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
            title="Close"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Pinned panel scrolls; hover panel is a fixed peek */}
      <div className={`px-3 py-2 ${pinned ? 'max-h-[420px] overflow-y-auto' : ''}`}>
        {run ? (
          <>
            <div className="mb-1 flex items-center gap-1.5">
              <GitBranch className="h-3 w-3 shrink-0 text-stone-400" />
              <span className="truncate font-mono text-[10px] text-stone-700">{run.head_branch || '—'}</span>
              <span className="font-mono text-[10px] text-stone-400">{shortSha(run.head_sha)}</span>
              {run.event && <span className="text-[9px] text-stone-400">· {run.event}</span>}
              {run.run_number != null && <span className="text-[9px] text-stone-400">· run #{run.run_number}</span>}
              {run.html_url && (
                <a
                  href={run.html_url} target="_blank" rel="noreferrer"
                  className="ml-auto shrink-0 text-stone-400 transition-colors hover:text-stone-600"
                  title="Open on GitHub"
                  onClick={e => e.stopPropagation()}
                >
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>

            <p className="mb-2 text-[10px] text-stone-500">
              {run.created_at ? `merged ${timeAgo(run.created_at)}` : ''}
              {runElapsed !== null && (
                <>
                  {run.created_at ? ' · ' : ''}
                  <span className={isRunning ? 'text-yellow-600' : ''}>
                    {isRunning ? 'running for ' : 'took '}
                    <span className="font-mono tabular-nums">{formatDuration(runElapsed)}</span>
                  </span>
                </>
              )}
            </p>

            <StepWindow lines={lines} />
          </>
        ) : (
          <p className="py-2 text-center text-[10px] text-stone-400">No runs recorded for this repo</p>
        )}

        {pinned && (
          <div className="mt-2 border-t border-stone-100 pt-2">
            <p className="mb-1 text-[9px] uppercase tracking-wide text-stone-400">Branches</p>
            <BranchBlock loading={graphLoading} error={graphError} summary={summary} />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 border-t border-stone-100 px-3 py-2">
        {deployLocked ? (
          <span className="flex items-center gap-1 text-[10px] text-red-500">
            <Lock className="h-3 w-3" /> Deploys locked for this repo
          </span>
        ) : (
          <>
            <button
              onClick={actions.onStart}
              disabled={isRunning}
              className="flex items-center gap-1 rounded border border-stone-200 px-2 py-1 text-[10px] text-stone-600 transition-colors hover:bg-stone-50 disabled:opacity-40"
            >
              <Play className="h-2.5 w-2.5" /> Start
            </button>
            <button
              onClick={actions.onStop}
              disabled={!isRunning}
              className="flex items-center gap-1 rounded border border-stone-200 px-2 py-1 text-[10px] text-red-500 transition-colors hover:bg-red-50 disabled:opacity-40"
            >
              <Square className="h-2.5 w-2.5" /> Stop
            </button>
            <button
              onClick={actions.onRerun}
              disabled={isRunning}
              className="flex items-center gap-1 rounded border border-stone-200 px-2 py-1 text-[10px] text-stone-600 transition-colors hover:bg-stone-50 disabled:opacity-40"
            >
              <RotateCw className="h-2.5 w-2.5" /> Rerun
            </button>
          </>
        )}
        <span className="ml-auto text-[9px] text-stone-300">
          {pinned ? 'Esc to close' : 'click to pin'}
        </span>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      ref={anchorRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onClick={handleClick}
      className={`cursor-pointer rounded transition-colors ${pinned ? 'bg-stone-100' : 'hover:bg-stone-50'}`}
    >
      {children}
      {panel}
    </div>
  );
}
